// ScrollableTabsList.tsx

import React, { useRef, useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import ScrollButton from 'ui/ScrollButton';
import { setDocumentTitle, setDefaultSiteOg } from 'util/ogMetadataUtils';
import { capitalizeFirstLetter } from 'util/textUtils';

interface TabItem {
  label: string;
  route?: string;         // e.g. '/user/{username}/overview'
  value: string;         // e.g. 'overview' or 'posts'
}

interface ScrollableTabsListProps {
  tabs: TabItem[];
  navlink?: boolean;
  selectedTab: string;
  pageTitle: string;
  onTabChange: (newTab: string) => void;
  className?: string;    // optional for styling
}

const ScrollableTabsList: React.FC<ScrollableTabsListProps> = ({
  tabs,
  navlink = true,
  selectedTab,
  pageTitle,
  onTabChange,
  className = '',
}) => {
  const navRef = useRef<HTMLDivElement>(null);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);

  setDocumentTitle(`${pageTitle} - ${capitalizeFirstLetter(selectedTab)}`);
  useEffect(() => {
    setDefaultSiteOg({ title: document.title, url: window.location.href });
  }, [pageTitle, selectedTab]);

  // Check if scroll buttons are needed
  const checkForScrollButtons = () => {
    if (!navRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = navRef.current;
    // Adjust as needed for off-by-one issues
    setShowLeftScroll(scrollLeft > 1);
    setShowRightScroll(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 1);
  };

  useEffect(() => {
    // Re-check on mount or whenever selectedTab changes
    checkForScrollButtons();
  }, [selectedTab]);

  // Check on window resize
  useEffect(() => {
    const handleResize = () => {
      checkForScrollButtons();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // On scroll event in the navRef, re-check
  const handleScroll = () => {
    checkForScrollButtons();
  };

  // Scroll left/right by fixed amount
  const scroll = (direction: 'left' | 'right') => {
    if (navRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      navRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className={`relative flex items-center overflow-hidden ${className}`}>
      {/* Left Scroll Button */}
      <ScrollButton
        direction="left"
        onClick={() => scroll('left')}
        visible={showLeftScroll}
      />

      {/* Scrollable Nav */}
      <div
        ref={navRef}
        onScroll={handleScroll}
        className="flex space-x-4 overflow-x-auto whitespace-nowrap scrollbar-hide w-full"
      >
        {tabs.map((tabItem) => (
          navlink ? (
          <NavLink
            key={tabItem.value}
            to={tabItem.route || ''}
            className={({ isActive }) =>
              selectedTab === tabItem.value
                ? 'tab-active flex-shrink-0'
                : 'tab-inactive flex-shrink-0'
            }
            onClick={() => onTabChange(tabItem.value)}
          >
              {tabItem.label}
            </NavLink>
          ) : (
            <div
              key={tabItem.value}
              className={
                selectedTab === tabItem.value
                  ? 'tab-active flex-shrink-0 cursor-pointer select-none'
                  : 'tab-inactive flex-shrink-0 cursor-pointer select-none'
              }
              onClick={() => onTabChange(tabItem.value)}
            >
              {tabItem.label}
            </div>
          )
        ))}
      </div>

      {/* Right Scroll Button */}
      <ScrollButton
        direction="right"
        onClick={() => scroll('right')}
        visible={showRightScroll}
      />
    </div>
  );
};

export default ScrollableTabsList;
